import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from 'services'

/**
 * VRG-655 — "Refer a brand", the brand-facing half. Endpoints are VRG-654's
 * (`apps/api/src/infrastructure/controllers/referrals.admin.controller.ts`), all
 * company-scoped by the API.
 *
 * Types mirror the API's shapes key-for-key (`referral-queries.use-case.ts`); the two
 * MUST change together.
 */

/**
 * The programme's numbers, from `REFERRAL_OFFER` (`domain/referrals/referral.constants.ts`).
 *
 * Values only — the COPY is the UI's (`constants/referrals.ts`). The
 * modal, the page and the invite email all read these same values, so the copy can never
 * quote one discount while the coupon applies another.
 */
export interface ReferralOffer {
  referee: {
    monthly_discount_percent: number
    monthly_discount_months: number
    annual_discount_percent: number
    applies_to: 'base_fee'
  }
  referrer: {
    reward_percent: number
    cents_per_activation: number
    floor_activations: number
    cap_activations: number
    window_months: number
  }
}

export interface ReferralLink {
  /** Null when the company cannot refer (demo / example). */
  code: string | null
  url: string | null
  can_refer: boolean
  offer: ReferralOffer
}

/** Per-address result of an invite. See `INVITE_OUTCOME_COPY` for why `sent` is broad. */
export type ReferralInviteOutcome =
  | 'sent'
  | 'already_referred'
  | 'ineligible_self_referral'
  | 'not_sent_region_paused'
  | 'failed'

export interface SendReferralInvitesBody {
  emails: string[]
  name?: string
  company_name?: string
  note?: string
}

export interface SendReferralInvitesResult {
  results: { email: string; outcome: ReferralInviteOutcome }[]
}

/**
 * The status a SENDER sees (`memberStatus` on the API). A prospect who is already a customer,
 * already waiting, or someone else's referral reads `invited` — the API never tells a sender
 * who is a Vera customer.
 */
export type MemberReferralStatus =
  | 'invited'
  | 'signed_up'
  | 'approved'
  | 'joined'
  | 'paying'
  | 'capped'
  | 'ended'
  | 'ineligible'

export interface MemberReferral {
  id: string
  channel: 'link' | 'email_invite'
  status: MemberReferralStatus
  invitee: { email: string | null; name: string | null; company_name: string | null }
  referred_company_name: string | null
  /** Only on the company-wide list (`scope=company`). */
  sender?: { user_id: string | null; name: string | null; email: string | null }
  created_at: string
  invite_sent_at: string | null
  first_paid_at: string | null
  reward_window_ends_at: string | null
  credits: { net: number; cap: number }
}

export type ReferralScope = 'mine' | 'company'

export interface MemberReferralsPage {
  data: MemberReferral[]
  total: number
  scope: ReferralScope
}

export interface ReferralSummary {
  company: {
    referrals: number
    paying_referrals: number
    credits_earned: number
    credits_reversed: number
    credits_net: number
  }
  mine: { referrals: number; paying_referrals: number; credits_net: number }
  cap_per_referral: number
  offer: ReferralOffer
}

const BASE = '/referrals/admin'

export const REFERRALS_QUERY_KEY = ['referrals', 'me'] as const

/**
 * The caller's link, minted on first call. Idempotent server-side, so it is safe to fetch
 * whenever the modal opens — it is NOT fetched by the sidebar, which would mint a code for
 * every member on their first page load whether or not they ever refer anyone.
 */
export const useMyReferralLink = (options?: { enabled?: boolean }) =>
  useQuery<ReferralLink>({
    queryKey: [...REFERRALS_QUERY_KEY, 'link'],
    queryFn: async () => (await api.get(`${BASE}/me/link`)).data,
    enabled: options?.enabled !== false,
    staleTime: 1000 * 60 * 30,
  })

export const useMyReferrals = (
  scope: ReferralScope,
  options?: { enabled?: boolean; limit?: number; offset?: number },
) =>
  useQuery<MemberReferralsPage>({
    queryKey: [...REFERRALS_QUERY_KEY, 'list', scope, options?.limit ?? 200, options?.offset ?? 0],
    queryFn: async () => {
      const params = new URLSearchParams({
        scope,
        limit: String(options?.limit ?? 200),
        offset: String(options?.offset ?? 0),
      })
      return (await api.get(`${BASE}/me?${params.toString()}`)).data
    },
    enabled: options?.enabled !== false,
  })

export const useReferralSummary = (options?: { enabled?: boolean }) =>
  useQuery<ReferralSummary>({
    queryKey: [...REFERRALS_QUERY_KEY, 'summary'],
    queryFn: async () => (await api.get(`${BASE}/me/summary`)).data,
    enabled: options?.enabled !== false,
    staleTime: 1000 * 60,
  })

/**
 * Send invites. Every referral read is invalidated on success: the page's list and totals,
 * and the Home checklist's "Refer a brand" tick, which reads the summary.
 */
export const useSendReferralInvites = () => {
  const queryClient = useQueryClient()
  return useMutation<SendReferralInvitesResult, unknown, SendReferralInvitesBody>({
    mutationFn: async (body) => (await api.post(`${BASE}/me/invites`, body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REFERRALS_QUERY_KEY })
    },
  })
}
